import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, X, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useImpersonation } from '@/hooks/useImpersonation';
import { captureError } from '@/lib/sentry';

const SESSION_CAP_MINUTES = 60;
const WARN_AT_MINUTES = 55;

/**
 * Persistent red banner shown to any session being driven by a
 * superadmin emulation. Renders at the very top of the app (above
 * AdminBar / MainNav) so it can never be hidden by app chrome.
 *
 * Auto-exits at the 60-minute cap; warns at 55. Cmd/Ctrl+Shift+E is a
 * keyboard exit affordance.
 */
export function ImpersonationBanner(): JSX.Element | null {
  const { state, end, isEnding } = useImpersonation();
  const { toast } = useToast();
  const [now, setNow] = useState(() => Date.now());
  const [warned, setWarned] = useState(false);

  const handleStop = useCallback(async () => {
    try {
      await end();
      toast({ title: 'Emulation ended', description: 'Restored your admin session.' });
    } catch (err) {
      captureError(err as Error, { feature: 'impersonation-end' });
      toast({
        title: 'Could not restore admin session',
        description: 'Please sign in again with your admin account.',
        variant: 'destructive',
      });
      // end() clears the stash before it throws, so the emulation is
      // already over server-side and client-side — but this component's
      // `state` won't flip until an auth event re-runs reconcile, which
      // may never come if setSession failed. Force a clean exit to /login
      // rather than leave the user stuck behind a banner whose Stop button
      // is now a no-op. Short delay so the toast is readable first.
      setTimeout(() => window.location.assign('/login'), 1500);
    }
  }, [end, toast]);

  useEffect(() => {
    if (!state.active) return;
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, [state.active]);

  useEffect(() => {
    if (!state.active) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'e') {
        e.preventDefault();
        void handleStop();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.active, handleStop]);

  // Warn near the cap, force-exit at the cap. These are side effects
  // (toast / session swap) and MUST run in an effect, never during render.
  // The previous version ran toast() and handleStop() inline in the render
  // body: toast() triggered React's "update a component while rendering a
  // different component" error, and the cap branch fired handleStop() on
  // every render — end() flips isEnding → re-render → handleStop again →
  // a re-render loop (React error #300) that crashed the banner and left
  // the Stop button dead.
  useEffect(() => {
    if (!state.active) return;
    const startedMs = new Date(state.started_at).getTime();
    const elapsedMin = Math.floor((now - startedMs) / 60_000);

    if (elapsedMin >= SESSION_CAP_MINUTES) {
      void handleStop();
      return;
    }
    if (elapsedMin >= WARN_AT_MINUTES && !warned) {
      setWarned(true);
      toast({
        title: `Emulation ends in ${SESSION_CAP_MINUTES - elapsedMin} min`,
        description: 'Wrap up — you will be returned to your admin session shortly.',
      });
    }
  }, [state, now, warned, handleStop, toast]);

  if (!state.active) return null;

  const startedMs = new Date(state.started_at).getTime();
  const elapsedMin = Math.floor((now - startedMs) / 60_000);
  const remainingMin = SESSION_CAP_MINUTES - elapsedMin;

  return (
    <div
      role="alert"
      className="sticky top-0 z-[100] w-full bg-red-600 text-white shadow-md"
    >
      <div className="mx-auto max-w-7xl px-4 py-2 flex items-center gap-3">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
        <div className="flex-1 min-w-0 text-sm">
          <span className="font-medium">Emulating </span>
          <span className="font-mono truncate">{state.target_email}</span>
          <span className="opacity-90 hidden sm:inline">
            {' · started '}
            {format(startedMs, 'p')}
            {' · ends in '}
            {Math.max(0, remainingMin)}
            {'m'}
          </span>
        </div>
        {state.read_only && (
          <span className="inline-flex items-center gap-1 rounded bg-white/20 px-2 py-0.5 text-xs">
            <Eye className="h-3 w-3" /> Read-only
          </span>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleStop}
          disabled={isEnding}
          className="text-white hover:bg-white/20 h-7 px-2"
        >
          <X className="h-3.5 w-3.5 mr-1" />
          {isEnding ? 'Stopping…' : 'Stop emulating'}
        </Button>
      </div>
    </div>
  );
}
